import React, { useState, useEffect, useCallback } from 'react';
import { getHealthInsuranceList, API_BASE_URL } from '../../model/api';
import LoadingSpinner from './common/LoadingSpinner';
import { toast } from 'react-toastify';
import ExpiringPoliciesTab from './ExpiringPoliciesTab';

const HealthInsuranceTab = ({ userRole }) => {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));

    // Filter Constants
    const currentYear = new Date().getFullYear();
    const startYear = 2026;
    const endYear = Math.max(2036, currentYear + 10);
    const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
    const months = [
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
    ];

    const handleYearChange = (e) => {
        const [_, month] = filterMonth.split('-');
        setFilterMonth(`${e.target.value}-${month}`);
    };

    const handleMonthSelectChange = (e) => {
        const [year, _] = filterMonth.split('-');
        setFilterMonth(`${year}-${e.target.value}`);
    };
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSubTab, setActiveSubTab] = useState('policies'); // 'policies' or 'expiring'

    const fetchPolicies = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterMonth) params.month = filterMonth;
            if (searchTerm) params.search = searchTerm;

            const result = await getHealthInsuranceList(params);
            if (result.ok) {
                setPolicies(result.data || []);
            } else {
                setPolicies([]);
                toast.error('Failed to fetch health insurance policies');
            }
        } catch (error) {
            console.error('Error fetching health insurance policies:', error);
            toast.error('Error loading policies');
        } finally {
            setLoading(false);
        }
    }, [filterMonth, searchTerm]);

    useEffect(() => {
        if (activeSubTab === 'policies') {
            fetchPolicies();
        }
    }, [activeSubTab, fetchPolicies]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'expired': return 'bg-red-100 text-red-800';
            case 'cancelled': return 'bg-gray-100 text-gray-800';
            default: return 'bg-yellow-100 text-yellow-800';
        }
    };

    return (
        <div className="space-y-6">
            {/* Sub-Tab Navigation */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveSubTab('policies')}
                        className={`${activeSubTab === 'policies'
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                    >
                        All Policies
                    </button>
                    <button
                        onClick={() => setActiveSubTab('expiring')}
                        className={`${activeSubTab === 'expiring'
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                    >
                        Expiring Soon
                        <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                            30 days
                        </span>
                    </button>
                </nav>
            </div>

            {/* Content based on active sub-tab */}
            {activeSubTab === 'expiring' ? (
                <ExpiringPoliciesTab />
            ) : (
                <>
                    {/* Filters */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                                <input
                                    type="text"
                                    placeholder="Policy No, Customer Name..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Month</label>
                                <div className="flex gap-2">
                                    <select
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                        value={filterMonth.split('-')[1]}
                                        onChange={handleMonthSelectChange}
                                    >
                                        {months.map(m => (
                                            <option key={m.value} value={m.value}>{m.label}</option>
                                        ))}
                                    </select>
                                    <select
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                        value={filterMonth.split('-')[0]}
                                        onChange={handleYearChange}
                                    >
                                        {years.map(y => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={fetchPolicies}
                                    className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
                                >
                                    Apply Filters
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Policies List */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-800">My Health Insurance Policies</h3>
                        </div>

                        {loading ? (
                            <div className="p-8"><LoadingSpinner /></div>
                        ) : policies.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                No health insurance policies found.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Policy No</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Premium</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documents</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {policies.map((policy) => (
                                            <tr key={policy._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{policy.policy_number}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <div>{policy.customer_name}</div>
                                                    <div className="text-xs text-gray-400">{policy.customer_phone}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{policy.plan_name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div>
                                                        <div className="font-medium">Total: ₹{policy.premium_amount?.toLocaleString('en-IN')}</div>
                                                        {policy.premium_by_year && policy.premium_by_year.length > 0 && policy.premium_by_year.map((p, idx) => (
                                                            <div key={idx} className="text-xs text-gray-500 mt-0.5">
                                                                Year {p.year}: ₹{Number(p.premium_amount).toLocaleString('en-IN')}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                                                    {(() => {
                                                        const totalComm = policy.commission_amount || 0;
                                                        const hasBreakdown = policy.premium_by_year && policy.premium_by_year.length > 0;
                                                        const avgPercent = policy.premium_amount ? ((totalComm / policy.premium_amount) * 100).toFixed(1) : 0;

                                                        return (
                                                            <div className="flex flex-col">
                                                                {hasBreakdown ? (
                                                                    <div className="text-green-600 font-medium whitespace-nowrap">
                                                                        Total: ₹{totalComm.toLocaleString('en-IN')}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-green-600 font-medium whitespace-nowrap">
                                                                        ₹{totalComm.toLocaleString('en-IN') || 0}
                                                                        <span className="text-xs text-gray-500 ml-1">({avgPercent}%)</span>
                                                                    </span>
                                                                )}

                                                                {hasBreakdown && policy.premium_by_year.map((p, idx) => {
                                                                    const commAmt = p.commission_amount || (Number(p.premium_amount) * (Number(p.commission_percentage || 0) / 100));
                                                                    return (
                                                                        <div key={idx} className="text-xs text-gray-500 mt-0.5 whitespace-nowrap">
                                                                            Y{p.year}: ₹{commAmt.toLocaleString('en-IN')} ({p.commission_percentage}%)
                                                                        </div>
                                                                    );
                                                                })}

                                                                {policy.is_renewal && policy.review_commission_amount > 0 && (
                                                                    <div className="text-xs text-green-500 mt-1">
                                                                        + ₹{policy.review_commission_amount?.toLocaleString('en-IN') || 0} (Bonus {policy.review_commission_percentage}%)
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {new Date(policy.policy_expires_date).toLocaleDateString('en-IN')}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(policy.status)}`}>
                                                        {policy.status?.charAt(0).toUpperCase() + policy.status?.slice(1)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold leading-5 rounded-full ${policy.payout_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                                            }`}>
                                                            {policy.payout_status === 'paid' ? 'Paid' : 'Pending'}
                                                        </span>
                                                        {policy.payout_status === 'paid' && policy.payout_transaction_id && (
                                                            <div className="mt-1 flex flex-col items-start gap-0.5">
                                                                <span className="text-[10px] text-gray-500 font-medium bg-gray-50 px-1 py-0.5 rounded border border-gray-100">
                                                                    {policy.payout_transaction_id}
                                                                </span>
                                                                {(policy.payout_payment_date || policy.payout_paid_at) && (
                                                                    <span className="text-[10px] text-gray-400 pl-0.5">
                                                                        {new Date(policy.payout_payment_date || policy.payout_paid_at).toLocaleDateString('en-IN', {
                                                                            day: '2-digit', month: 'short', year: '2-digit'
                                                                        })}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-600">
                                                    {policy.document_pdf_url && (
                                                        <a href={policy.document_pdf_url} target="_blank" rel="noopener noreferrer" className="hover:underline mr-2">
                                                            Policy
                                                        </a>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )
            }
        </div >
    );
};

export default HealthInsuranceTab;
